import { EV } from './EventBus.js';
import { clamp } from '../util/math.js';

/**
 * Pointer-lock mouse + keyboard + gamepad, normalized into a single action map.
 *
 * Design notes that matter for feel:
 *  - Mouse deltas are accumulated raw and drained once per frame. Never smooth
 *    or interpolate them; any filtering here reads as input lag.
 *  - Sensitivity is applied in the camera rig, not here, so ADS sens scaling
 *    stays a rendering/aiming concern.
 *  - Actions are edge-tracked (`pressed` vs `down`) so weapon code can ask
 *    "was fire tapped this frame" for semi-auto without polling races.
 */

export const ACTIONS = {
  FORWARD: 'forward', BACK: 'back', LEFT: 'left', RIGHT: 'right',
  JUMP: 'jump', CROUCH: 'crouch', SPRINT: 'sprint',
  FIRE: 'fire', ADS: 'ads', RELOAD: 'reload', MELEE: 'melee',
  SWITCH: 'switch', SLOT1: 'slot1', SLOT2: 'slot2', SLOT3: 'slot3', GRENADE: 'grenade',
  USE: 'use', LEAN_L: 'leanL', LEAN_R: 'leanR',
  PAUSE: 'pause', SCORE: 'scoreboard', FLASHLIGHT: 'flashlight',
  // Chat de voz por proximidade: B abre/fecha o próprio microfone, V silencia
  // ou reativa a voz dos outros.
  VOICE_MIC: 'voiceMic', VOICE_DEAF: 'voiceDeaf',
};

const DEFAULT_BINDS = {
  KeyW: ACTIONS.FORWARD, ArrowUp: ACTIONS.FORWARD,
  KeyS: ACTIONS.BACK, ArrowDown: ACTIONS.BACK,
  KeyA: ACTIONS.LEFT, ArrowLeft: ACTIONS.LEFT,
  KeyD: ACTIONS.RIGHT, ArrowRight: ACTIONS.RIGHT,
  Space: ACTIONS.JUMP,
  ControlLeft: ACTIONS.CROUCH, KeyC: ACTIONS.CROUCH,
  ShiftLeft: ACTIONS.SPRINT,
  KeyR: ACTIONS.RELOAD,
  KeyQ: ACTIONS.LEAN_L, KeyE: ACTIONS.LEAN_R,
  KeyF: ACTIONS.USE,
  KeyG: ACTIONS.GRENADE,
  KeyL: ACTIONS.FLASHLIGHT,
  Digit1: ACTIONS.SLOT1, Digit2: ACTIONS.SLOT2, Digit3: ACTIONS.SLOT3,
  // V saiu do corpo a corpo para a voz, a pedido de quem joga. A faca continua
  // acessível pela tecla 3 (slot próprio) e pelo botão do meio do mouse, então
  // nada ficou sem atalho — e uma tecla mapeada duas vezes no mesmo objeto some
  // em silêncio, que é pior que qualquer conflito declarado.
  KeyB: ACTIONS.VOICE_MIC, KeyV: ACTIONS.VOICE_DEAF,
  Tab: ACTIONS.SCORE,
  Escape: ACTIONS.PAUSE,
};

const MOUSE_BINDS = { 0: ACTIONS.FIRE, 2: ACTIONS.ADS, 1: ACTIONS.MELEE, 3: ACTIONS.SWITCH };

export class Input {
  constructor(bus, element) {
    this.bus = bus;
    this.el = element;
    this.binds = { ...DEFAULT_BINDS };

    this.down = new Set();        // held this frame
    this.pressed = new Set();     // went down since last drain
    this.released = new Set();    // went up since last drain

    this.mouseDX = 0; this.mouseDY = 0;
    this.wheel = 0;
    this.locked = false;
    this.enabled = true;

    this.gamepadIndex = -1;
    this.stickL = { x: 0, y: 0 };
    this.stickR = { x: 0, y: 0 };
    this.gamepadDeadzone = 0.14;

    this._bind();
  }

  _bind() {
    const opts = { passive: false };
    this._onKeyDown = (e) => {
      if (e.repeat) return;
      const a = this.binds[e.code];
      if (a) { e.preventDefault(); this._press(a); }
      // Escape is handled by the browser to exit pointer lock; still notify UI.
      if (e.code === 'Escape') this.bus.emit(EV.ACTION, { action: ACTIONS.PAUSE, pressed: true });
    };
    this._onKeyUp = (e) => { const a = this.binds[e.code]; if (a) { e.preventDefault(); this._release(a); } };
    this._onMouseDown = (e) => {
      if (!this.locked) return;
      const a = MOUSE_BINDS[e.button];
      if (a) { e.preventDefault(); this._press(a); }
    };
    this._onMouseUp = (e) => { const a = MOUSE_BINDS[e.button]; if (a) this._release(a); };
    this._onMouseMove = (e) => {
      if (!this.locked || !this.enabled) return;
      // movementX/Y can spike absurdly on some drivers when the pointer is
      // re-locked; clamping avoids a full 180 spin on lock.
      this.mouseDX += clamp(e.movementX || 0, -400, 400);
      this.mouseDY += clamp(e.movementY || 0, -400, 400);
    };
    this._onWheel = (e) => { if (this.locked) { e.preventDefault(); this.wheel += Math.sign(e.deltaY); } };
    this._onContext = (e) => e.preventDefault();
    this._onLockChange = () => {
      this.locked = document.pointerLockElement === this.el;
      if (!this.locked) { this.down.clear(); this.mouseDX = this.mouseDY = 0; }
      this.bus.emit(EV.POINTER_LOCK, { locked: this.locked });
    };
    this._onBlur = () => { this.down.clear(); this.mouseDX = this.mouseDY = 0; };

    window.addEventListener('keydown', this._onKeyDown, opts);
    window.addEventListener('keyup', this._onKeyUp, opts);
    window.addEventListener('mousedown', this._onMouseDown, opts);
    window.addEventListener('mouseup', this._onMouseUp, opts);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('wheel', this._onWheel, opts);
    window.addEventListener('contextmenu', this._onContext);
    window.addEventListener('blur', this._onBlur);
    document.addEventListener('pointerlockchange', this._onLockChange);
    window.addEventListener('gamepadconnected', (e) => { this.gamepadIndex = e.gamepad.index; });
    window.addEventListener('gamepaddisconnected', () => { this.gamepadIndex = -1; });
  }

  _press(a) { if (!this.down.has(a)) { this.down.add(a); this.pressed.add(a); this.bus.emit(EV.ACTION, { action: a, pressed: true }); } }
  _release(a) { if (this.down.has(a)) { this.down.delete(a); this.released.add(a); this.bus.emit(EV.ACTION, { action: a, pressed: false }); } }

  requestLock() { if (!this.locked) this.el.requestPointerLock?.({ unadjustedMovement: true }); }
  exitLock() { if (this.locked) document.exitPointerLock?.(); }

  isDown(a) { return this.down.has(a); }
  wasPressed(a) { return this.pressed.has(a); }
  wasReleased(a) { return this.released.has(a); }

  /** Normalized WASD vector, magnitude clamped to 1 (no diagonal speed boost). */
  moveVector(out = { x: 0, y: 0 }) {
    let x = (this.isDown(ACTIONS.RIGHT) ? 1 : 0) - (this.isDown(ACTIONS.LEFT) ? 1 : 0);
    let y = (this.isDown(ACTIONS.FORWARD) ? 1 : 0) - (this.isDown(ACTIONS.BACK) ? 1 : 0);
    if (this.gamepadIndex >= 0 && (Math.abs(this.stickL.x) + Math.abs(this.stickL.y)) > 0) {
      x = this.stickL.x; y = -this.stickL.y;
    }
    const len = Math.hypot(x, y);
    if (len > 1) { x /= len; y /= len; }
    out.x = x; out.y = y;
    return out;
  }

  _pollGamepad() {
    if (this.gamepadIndex < 0 || !navigator.getGamepads) return;
    const gp = navigator.getGamepads()[this.gamepadIndex];
    if (!gp) return;
    const dz = (v) => (Math.abs(v) < this.gamepadDeadzone ? 0
      : Math.sign(v) * ((Math.abs(v) - this.gamepadDeadzone) / (1 - this.gamepadDeadzone)) ** 1.6);
    this.stickL.x = dz(gp.axes[0] || 0); this.stickL.y = dz(gp.axes[1] || 0);
    this.stickR.x = dz(gp.axes[2] || 0); this.stickR.y = dz(gp.axes[3] || 0);

    const btn = (i) => !!gp.buttons[i]?.pressed;
    const map = [
      [0, ACTIONS.JUMP], [1, ACTIONS.CROUCH], [2, ACTIONS.RELOAD], [3, ACTIONS.SWITCH],
      [7, ACTIONS.FIRE], [6, ACTIONS.ADS], [10, ACTIONS.SPRINT], [5, ACTIONS.GRENADE], [4, ACTIONS.MELEE],
    ];
    for (const [i, a] of map) { if (btn(i)) this._press(a); else this._release(a); }
  }

  /** Drain per-frame accumulators. Call exactly once, after all systems read. */
  endFrame() {
    this.pressed.clear();
    this.released.clear();
    this.mouseDX = 0; this.mouseDY = 0; this.wheel = 0;
  }

  beginFrame() { this._pollGamepad(); }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('wheel', this._onWheel);
    window.removeEventListener('contextmenu', this._onContext);
    window.removeEventListener('blur', this._onBlur);
    document.removeEventListener('pointerlockchange', this._onLockChange);
  }
}
